import React, { useContext, useEffect, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import TotalBalanceBlue from "../../svg/TotalBalanceBlue";
import TotalBalanceGreen from "../../svg/TotalBalanceGreen";
import TotalBalanceOrange from "../../svg/TotalBalanceOrange";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import axiosInstance from "../../../utils/Requests/axios";
import UserContext from "../../../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import Constant from "../../Constants";
import ThemeContext from "../../../utils/Contexts/ThemeContext";
import GraphDashboard from "../../svg/graphDashboard";

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = (
  totalBalance,
  translatedLabel,
  bgColor,
  containerSelector
) => ({
  id: "centerText",
  beforeDraw(chart) {
    const { width, height } = chart;
    const ctx = chart.ctx;

    const container =
      document.querySelector(containerSelector) || document.documentElement;
    const cssVarValue =
      getComputedStyle(container).getPropertyValue(bgColor).trim() || "#000";

    // Defensive check
    if (!chart._metasets?.[0]?.data?.[0]) return;

    ctx.save();

    const centerX = width / 2;
    const centerY = height / 2;

    const innerRadius = chart._metasets[0].data[0].innerRadius * 0.9;

    ctx.beginPath();
    ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
    ctx.fillStyle = cssVarValue;

    ctx.fill();

    ctx.restore();
    // Calculate total balance safely
    let total = 0;
    if (totalBalance) {
      const values = Object.values(totalBalance).map((v) => parseFloat(v) || 0);
      total = values.reduce((sum, v) => sum + v, 0);
    }

    // Format number as currency
    const formattedTotal =
      total % 1 === 0
        ? `$${total.toLocaleString()}`
        : `$${total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} `;

    const ismedScreen = window.innerWidth < 1920;
    const isSmallScreen = window.innerWidth < 1024;

    const mainFontSize = isSmallScreen ? 28 : ismedScreen ? 28 : 36;
    const subTextFontSize = isSmallScreen ? 14 : ismedScreen ? 14 : 16;
    // Draw big value text
    ctx.fillStyle = "rgba(255, 255, 255, 1)";
    ctx.font = `700 ${mainFontSize}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(formattedTotal, centerX, centerY - 10);

    ctx.font = `400 ${subTextFontSize}px sans-serif`;
    ctx.fillStyle = "#6C747A";
    ctx.fillText(translatedLabel, centerX, centerY + 20);

    ctx.save();
  },
});

function TotalBalance() {
  const { t, language } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const [totalBalance, setTotalBalance] = useState(null);
  const { themeStatus } = Constant();
  const { selectedTheme } = useContext(ThemeContext);
  const totalBalanceFetched = useRef(false);
  const GetTotalBalance = () => {
    if (totalBalanceFetched.current) return;
    totalBalanceFetched.current = true;
    const url = `/users/${userData?.uuid}/wallet/balance`;
    axiosInstance
      .get(url)
      .then((response) => {
        setTotalBalance(response?.data);
      })
      .catch(() => {
        toast.error(t("There was an error fetching the wallet balance"));
      });
  };

  useEffect(() => {
    GetTotalBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = {
    labels: [t("Available"), t("On Hold"), t("Pending")],
    datasets: [
      {
        data: [
          totalBalance?.[200] || 0,
          totalBalance?.[300] || 0,
          totalBalance?.[100] || 0,
        ],
        backgroundColor: ["#17d4a3", "#f28c38", "#5c79ff"],
        borderColor: selectedTheme === "dark" ? "#25282a" : "#FFFFFF99",
        borderWidth: 3,
        cutout: "85%", // Like donut size
        borderRadius: 20, // <-- Rounded edges
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  return (
    <div
      className={`w-100 p-3 rounded seller-rating-height cardSetting ${themeStatus?.[selectedTheme]}`}
      style={{ backgroundColor: "var(--bg-card)" }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h4
          className="dashboard-title-responsive p-0 mb-0"
          style={{ lineHeight: "150%" }}
        >
          {t("Total Balance")}
        </h4>
        <div>
          <GraphDashboard fill={"var(--graph-icon)"} />
        </div>
      </div>
      <div
        className="my-3"
        style={{
          borderBottom: `2px solid var(--border)`,
          marginLeft: "-18px",
          marginRight: "-18px",
        }}
      ></div>
      <div className="d-flex seller-rating">
        <div id="chartDiv" className="d-flex seller-doughnut">
          {totalBalance && (
            <Doughnut
              key={`${language}-${selectedTheme}`}
              data={data}
              options={options}
              plugins={[
                centerTextPlugin(
                  totalBalance,
                  t("Total Balance"),
                  "--graph-bg",
                  `.${themeStatus[selectedTheme]}`
                ),
              ]}
            />
          )}
        </div>
        <div
          className="flex-column gap-2 seller-rating-labels-sm"
          style={{ marginTop: "0.5rem" }}
        >
          <div className="totalBalanceLabel">
            <TotalBalanceGreen /> {t("Available")}
          </div>
          <div className="totalBalanceLabel">
            <TotalBalanceOrange /> {t("On Hold")}
          </div>
          <div className="totalBalanceLabel">
            <TotalBalanceBlue /> {t("Pending")}
          </div>
          <button className="btn dashboard-balance-button mt-4 btn-dash-med">
            {t("Deposit")}
          </button>
          <button className="btn dashboard-balance-button btn-dash-med">
            {t("Withdraw")}
          </button>
        </div>
        <div className="seller-rating-labels-lg gap-3">
          <div className="d-flex justify-content-between">
            <div className="totalBalanceLabel">
              <TotalBalanceGreen /> {t("Available")}
            </div>
            <div className="totalBalanceLabel">
              <TotalBalanceOrange /> {t("On Hold")}
            </div>
            <div className="totalBalanceLabel">
              <TotalBalanceBlue /> {t("Pending")}
            </div>
          </div>

          <div className="d-flex gap-3">
            <button className="btn dashboard-balance-button">
              {t("Deposit")}
            </button>
            <button className="btn dashboard-balance-button">
              {t("Withdraw")}
            </button>
          </div>
        </div>
      </div>
      <div className="gap-3 btn-dash-sm mt-5 justify-content-center">
        <button className="btn dashboard-balance-button p-0">
          {t("Deposit")}
        </button>
        <button className="btn dashboard-balance-button p-0">
          {t("Withdraw")}
        </button>
      </div>
    </div>
  );
}

export default TotalBalance;
